import { useMemo, useState } from 'react';
import type { GetSessionsForViewerResponse } from '@usacsmideo/types';
import { DayGroupedSessions } from './DayGroupedSessions';
import { FamilyGroup } from './FamilyGroup';
import type { HandoffConsentProps } from './SessionCard';
import { RecencyFilter } from './RecencyFilter';
import {
  filterByRecency,
  shouldAutoExpand,
  type RecencyFilter as RecencyFilterValue,
} from './sessionGrouping';

/**
 * The converged `/sessions` surface (CU-86aj1c9ev / convergence redesign §2).
 * Pure-props (data + focus + clock injected) so it is unit-testable without
 * routing or the network. The route component owns data fetching and reads
 * `focusSessionId` from the path/search param.
 *
 * Two persona-gated sections:
 *   - "My Scans"          — rendered IFF the response carries own sessions
 *     (server only populates `myScans` for an isPatient caller).
 *   - "My Family's Scans" — rendered IFF the caller holds ≥1 accepted agent
 *     relationship (`family.length > 0`); grouped by patient, collapsible, with
 *     recency auto-expand (§2.2) and a `?focus=` override.
 *
 * A caller with neither (empty myScans + empty family) is "not configured" and
 * gets the helper message (redesign §8 / decision 1).
 */
export function SessionsSurface({
  data,
  focusSessionId,
  now,
  onRefresh,
  isRefreshing = false,
  onRecordHandoffConsent,
  isRecordingConsent = false,
}: {
  data: GetSessionsForViewerResponse;
  focusSessionId?: string;
  /** Injectable clock for deterministic recency tests; defaults to Date.now(). */
  now?: number;
  /**
   * CU-86aj3a0k2: refetch the sessions query. Threaded to the per-card "Check
   * for updates" button, shown only on a session still in its live window
   * (now < expiresAt), where new engagement badges can still arrive.
   */
  onRefresh?: () => void;
  isRefreshing?: boolean;
} & Pick<HandoffConsentProps, 'onRecordHandoffConsent' | 'isRecordingConsent'>) {
  const clock = now ?? Date.now();
  const [recency, setRecency] = useState<RecencyFilterValue>('all');

  // CU-86ajd3yt0: PER-VIEWER external-handoff suppression, derived from the
  // folded read path on the (injected) response. Absent ≡ not suppressed. The
  // mutation itself comes from the route (owns the callable); the surface stays
  // pure-props by deriving suppression from `data` rather than fetching.
  const dontAskAgain = data.handoffConsent?.dontAskAgain ?? false;
  const handoffProps: HandoffConsentProps = {
    dontAskAgain,
    onRecordHandoffConsent,
    isRecordingConsent,
  };

  // "My Scans" is only ever populated server-side for an isPatient caller, so
  // its presence is the patient gate (no separate persona prop needed here).
  const hasFamily = data.family.length > 0;
  const showMyScans = data.myScans.length > 0;

  const myScans = useMemo(
    () => filterByRecency(data.myScans, recency, clock),
    [data.myScans, recency, clock],
  );

  // Which family group (if any) the focused session belongs to — that group is
  // force-expanded regardless of recency.
  const focusGroupPatientId = useMemo(() => {
    if (!focusSessionId) return undefined;
    return data.family.find((g) =>
      g.sessions.some((s) => s.sessionId === focusSessionId),
    )?.patientId;
  }, [focusSessionId, data.family]);

  const familyGroups = useMemo(
    () =>
      data.family.map((g) => ({
        group: { ...g, sessions: filterByRecency(g.sessions, recency, clock) },
        defaultExpanded:
          g.patientId === focusGroupPatientId ||
          shouldAutoExpand(g.sessions, clock),
      })),
    [data.family, recency, clock, focusGroupPatientId],
  );

  const notConfigured = !showMyScans && !hasFamily;

  if (notConfigured) {
    return (
      <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold text-sky-800">Sessions</h1>
        <p className="text-gray-600">
          Your account is not configured to receive session information. Please
          contact support if you think this is an error.
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-sky-800">Sessions</h1>
        <RecencyFilter value={recency} onChange={setRecency} />
      </div>

      {showMyScans && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-gray-800">My Scans</h2>
          <DayGroupedSessions
            sessions={myScans}
            focusSessionId={focusSessionId}
            now={clock}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            {...handoffProps}
          />
        </section>
      )}

      {hasFamily && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-gray-800">
            My Family&apos;s Scans
          </h2>
          <div className="flex flex-col gap-3">
            {familyGroups.map(({ group, defaultExpanded }) => (
              <FamilyGroup
                key={group.patientId}
                group={group}
                defaultExpanded={defaultExpanded}
                focusSessionId={focusSessionId}
                now={clock}
                onRefresh={onRefresh}
                isRefreshing={isRefreshing}
                {...handoffProps}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default SessionsSurface;
